import { useEffect, useRef, useState } from 'react';
import { InvitationDto } from 'woofwoof-api';

export type InvitationAction = 'accept' | 'reject' | 'dashboard';

interface Options {
  invites: InvitationDto[];
  onAccept: (invitation: InvitationDto) => Promise<void>;
  onReject: (invitation: InvitationDto) => Promise<void>;
  onDashboard: () => Promise<void> | void;
}

export interface InvitationDecisions {
  pending: InvitationDto[];
  invitation?: InvitationDto;
  currentIndex: number;
  select: (index: number) => void;
  action: InvitationAction | null;
  error: boolean;
  /** The last decision was recorded and the user is being taken to the dashboard. */
  leaving: boolean;
  decide: (decision: InvitationAction) => Promise<void>;
}

export function useInvitationDecisions({
  invites,
  onAccept,
  onReject,
  onDashboard,
}: Options): InvitationDecisions {
  const [index, setIndex] = useState(0);
  const [resolved, setResolved] = useState<string[]>([]);
  const [action, setAction] = useState<InvitationAction | null>(null);
  const [error, setError] = useState(false);
  // The invitation being decided stays on screen however the list changes
  // meanwhile: callers drop it from their cache before this hook hears back.
  const [pinned, setPinned] = useState<InvitationDto | null>(null);
  const [leaving, setLeaving] = useState(false);
  const busy = useRef(false);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);
  const pending = invites.filter(
    (invite) =>
      invite.status === 'SENT' &&
      Date.parse(invite.expiresAt) > now &&
      !resolved.includes(invite.id),
  );
  const currentIndex = pending.length ? index % pending.length : 0;
  // While leaving, the decided card keeps its spinning button; after a failed
  // refresh it gives way to the dashboard retry so the decision cannot be replayed.
  const invitation = leaving
    ? error
      ? undefined
      : pinned ?? undefined
    : pinned ?? pending[currentIndex];

  const decide = async (decision: InvitationAction): Promise<void> => {
    if (busy.current) return;
    if (
      decision !== 'dashboard' &&
      (!invitation || Date.parse(invitation.expiresAt) <= Date.now())
    ) {
      setNow(Date.now());
      return;
    }
    busy.current = true;
    let leavingNow = false;
    setAction(decision);
    setError(false);
    try {
      if (decision === 'dashboard') {
        await onDashboard();
        return;
      }
      if (!invitation) return;
      setPinned(invitation);
      await (decision === 'accept'
        ? onAccept(invitation)
        : onReject(invitation));
      setResolved((previous) => [...previous, invitation.id]);
      // Each decision advances to the next invitation; the last one leaves.
      if (pending.length === 1) {
        leavingNow = true;
        setLeaving(true);
        await onDashboard();
      } else {
        setPinned(null);
      }
    } catch {
      setError(true);
      if (!leavingNow) setPinned(null);
    } finally {
      busy.current = false;
      setAction(null);
    }
  };

  return {
    pending,
    invitation,
    currentIndex,
    select: (next) =>
      setIndex(((next % pending.length) + pending.length) % pending.length),
    action,
    error,
    leaving,
    decide,
  };
}
